import * as React from 'react'

import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxOption } from '#/shared/ui/combobox'

import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useZipLookup } from '../model/use-zip-lookup'
import type { ZipLookupResult } from '../model/use-zip-lookup'

interface ZipComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onGeocodedSelect?: (geocoded: ZipLookupResult) => void
  onLoadingChange?: (loading: boolean) => void
  label?: string
  placeholder?: string
  error?: boolean
  className?: string
}

const ZIP_LENGTH = 5
const ALWAYS_PASS_FILTER = () => true

function findExact(
  results: ZipLookupResult[],
  trimmed: string,
  debouncedQuery: string,
): ZipLookupResult | undefined {
  if (trimmed.length !== ZIP_LENGTH) return undefined
  if (debouncedQuery !== trimmed) return undefined
  if (results.length !== 1) return undefined
  return results.find((r) => r.zip === trimmed)
}

function useZipAutoApply(
  trimmed: string,
  exactMatch: ZipLookupResult | undefined,
  appliedRef: React.MutableRefObject<string | null>,
  onGeocodedSelect?: (geocoded: ZipLookupResult) => void,
) {
  React.useEffect(() => {
    if (trimmed.length === 0) {
      appliedRef.current = null
      return
    }
    if (!exactMatch) return
    if (appliedRef.current === exactMatch.zip) return
    appliedRef.current = exactMatch.zip
    onGeocodedSelect?.(exactMatch)
  }, [trimmed, exactMatch, appliedRef, onGeocodedSelect])
}

export function ZipCombobox({
  value,
  onValueChange,
  onGeocodedSelect,
  onLoadingChange,
  label,
  placeholder,
  error,
  className,
}: ZipComboboxProps) {
  const trimmed = value.trim()
  const freeze = useComboboxFreeze<ZipLookupResult>(value)
  const { results, debouncedQuery, isLoading } = useZipLookup(value)
  const lastAppliedRef = React.useRef<string | null>(trimmed || null)

  const exactMatch = freeze.isJustSelected
    ? undefined
    : findExact(results, trimmed, debouncedQuery)

  useZipAutoApply(trimmed, exactMatch, lastAppliedRef, onGeocodedSelect)

  React.useEffect(() => {
    onLoadingChange?.(trimmed.length === ZIP_LENGTH && isLoading)
  }, [isLoading, trimmed, onLoadingChange])

  const items = freeze.pickItems(results)

  const options = React.useMemo<ComboboxOption[]>(() => {
    if (exactMatch) return []
    return items.map((r) => ({
      value: r.zip,
      label: r.zip,
      tag: `${r.city}, ${r.state}`,
    }))
  }, [items, exactMatch])

  const handleValueChange = (next: string) => {
    freeze.onTyped(next)
    onValueChange(next)
  }

  const handleSelect = (option: ComboboxOption) => {
    const result = items.find((r) => r.zip === option.value)
    if (!result) return
    freeze.freeze(option.label, items)
    lastAppliedRef.current = result.zip
    onGeocodedSelect?.(result)
  }

  return (
    <Combobox
      label={label}
      labelSize="sm"
      placeholder={placeholder}
      options={options}
      value={value}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      filter={ALWAYS_PASS_FILTER}
      loading={isLoading}
      hideEmpty
      hideToggle
      maxLength={ZIP_LENGTH}
      error={error}
      className={className}
      clearable
    />
  )
}
